import { ADMIN_GRANTABLE_PERMISSIONS, type AdminPermissionKey } from "@kawkaw/shared";
import { useAuthStore } from "../store/auth";

export type AdminRole = "superadmin" | "admin" | "stall_staff" | "pos_operator";

// Routes every admin/superadmin can always reach, regardless of granted permissions.
const BASELINE_ADMIN_ROUTES = ["/", "/orders", "/transactions"];

// Hardcoded superadmin-only — never individually grantable to a regular
// admin, either because they're platform-wide config (settings, feature
// flags, pricing rules, admin-account management) or because they're the
// most sensitive credentials in the system (payment/delivery provider
// secrets). Mirrors the backend's requireSuperadmin gates on these same
// routes' endpoints (middleware/auth.ts, admin/routes.ts).
const SUPERADMIN_ONLY_ROUTES = ["/settings", "/flags", "/pricing-rules", "/payment-methods", "/delivery-providers", "/sms-providers", "/admins"];

// Every other admin-app route, mapped to the permission key that grants it —
// the SAME keys enforced server-side by requirePermission() (middleware/auth.ts).
// Single source of truth for the keys is ADMIN_GRANTABLE_PERMISSIONS
// (packages/shared) — keep this route mapping in sync with the backend's
// route-level requirePermission calls when either changes.
const ROUTE_PERMISSION: Record<string, AdminPermissionKey> = {
  "/refunds": "approve:refunds",
  "/menu": "manage:menu",
  "/categories": "manage:menu",
  "/vouchers": "manage:vouchers",
  "/bundles": "manage:bundles",
  "/stalls": "manage:staff",
  "/stall-applications": "manage:staff",
  "/staff": "manage:staff",
  "/reports": "view:reports",
  "/activity": "view:reports",
  "/hours": "manage:hours",
  "/banners": "manage:promotions",
  "/legal-content": "manage:branding",
  "/customers": "manage:customers",
};

// pos_operator/stall_staff access is unaffected by the granular-permission
// system above — purely role-gated, unchanged from before.
const STALL_STAFF_ROUTES = ["/menu", "/categories", "/orders", "/reports"];
const POS_OPERATOR_ROUTES = ["/orders"];

export function usePermissions() {
  const staff = useAuthStore((s) => s.staff);
  const role = (staff?.role ?? "pos_operator") as AdminRole;
  const grantedPermissions = staff?.permissions ?? [];

  const canRoute = (path: string): boolean => {
    if (role === "superadmin") return true;
    if (role === "stall_staff") return STALL_STAFF_ROUTES.includes(path);
    if (role === "pos_operator") return POS_OPERATOR_ROUTES.includes(path);
    // role === "admin"
    if (SUPERADMIN_ONLY_ROUTES.includes(path)) return false;
    if (BASELINE_ADMIN_ROUTES.includes(path)) return true;
    const permission = ROUTE_PERMISSION[path];
    return permission ? grantedPermissions.includes(permission) : false;
  };

  // Mirrors the backend's requirePermission() narrowing exactly: superadmin
  // always allowed, admin only if the key is in their granted permissions.
  const can = (action: string): boolean => {
    if (role === "superadmin") return true;
    if (role === "admin") return grantedPermissions.includes(action);
    return action === "view:orders"; // the one thing every role can always do
  };

  const isSuperAdmin = role === "superadmin";
  const isAdmin = role === "superadmin" || role === "admin";
  const isStallStaff = role === "stall_staff";

  return { role, can, canRoute, isSuperAdmin, isAdmin, isStallStaff, permissions: grantedPermissions };
}

export { ADMIN_GRANTABLE_PERMISSIONS };
